package com.shadowings.gero.presentation.component.quiz

import androidx.compose.animation.core.tween
import androidx.compose.material3.MaterialTheme
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.pager.PagerState
import androidx.compose.foundation.pager.rememberPagerState
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.MutableState
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.rememberCoroutineScope
import com.shadowings.gero.util.SoundPlayer
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.semantics.testTag
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import androidx.navigation.NavController
import androidx.navigation.compose.rememberNavController
import com.shadowings.gero.domain.model.Faces
import com.shadowings.gero.domain.model.IQuizContent
import com.shadowings.gero.domain.model.UserData
import com.shadowings.gero.presentation.component.GeroButton
import com.shadowings.gero.presentation.ui.AppTheme
import com.shadowings.gero.presentation.util.DevicePreviews
import gero.composeapp.generated.resources.Res
import gero.composeapp.generated.resources.avanti_btn
import gero.composeapp.generated.resources.three_errors_feedback
import gero.composeapp.generated.resources.three_errors_subtitle
import gero.composeapp.generated.resources.what_happened_title
import kotlinx.coroutines.launch
import org.jetbrains.compose.resources.stringResource

@DevicePreviews
@Preview
@Preview(name = "Dark", uiMode = 0x20, showBackground = true)
@Composable
private fun QuizThreeErrorsComposablePreview() {
    AppTheme {
        Surface {
            QuizThreeErrorsComposable(
                page = 0,
                pagerState = rememberPagerState { 1 },
                navController = rememberNavController(),
                quizList = mutableStateOf(listOf()),
                userData = UserData()
            )
        }
    }
}

@Composable
fun QuizThreeErrorsComposable(
    page: Int,
    pagerState: PagerState,
    navController: NavController,
    quizList: MutableState<List<IQuizContent>>,
    userData: UserData
) {
    val scope = rememberCoroutineScope()
    val isVisible = pagerState.currentPage == page

    LaunchedEffect(isVisible) {
        if (isVisible) {
            SoundPlayer.playThreeErrors()
        }
    }

    androidx.compose.foundation.layout.BoxWithConstraints(
        Modifier.fillMaxSize().semantics { testTag = "quiz_three_errors" }) {
        val isLandscape = maxWidth > maxHeight

        if (isLandscape) {
            Row(
                modifier = Modifier
                    .fillMaxSize()
                    .padding(horizontal = 24.dp, vertical = 16.dp),
                horizontalArrangement = Arrangement.spacedBy(24.dp),
                verticalAlignment = Alignment.CenterVertically
            ) {
                // Column 1: Achievement Titles
                Column(
                    modifier = Modifier.weight(1f),
                    horizontalAlignment = Alignment.CenterHorizontally,
                    verticalArrangement = Arrangement.Center
                ) {
                    Text(
                        text = stringResource(Res.string.what_happened_title),
                        style = MaterialTheme.typography.titleLarge.copy(fontWeight = FontWeight.Bold),
                        textAlign = TextAlign.Center,
                        modifier = Modifier.fillMaxWidth()
                            .semantics { testTag = "three_errors_title" }
                    )
                    Text(
                        text = stringResource(Res.string.three_errors_subtitle),
                        style = MaterialTheme.typography.titleMedium,
                        textAlign = TextAlign.Center,
                        modifier = Modifier.fillMaxWidth()
                            .semantics { testTag = "three_errors_subtitle" }
                    )
                }

                // Column 2: Feedback & Button
                Column(
                    modifier = Modifier.weight(1f),
                    horizontalAlignment = Alignment.CenterHorizontally,
                    verticalArrangement = Arrangement.spacedBy(16.dp)
                ) {
                    AlynFeedbackBox(
                        face = Faces.Sad,
                        text = stringResource(Res.string.three_errors_feedback),
                        userData = userData,
                        isLandscape = true
                    )

                    GeroButton(
                        title = stringResource(Res.string.avanti_btn),
                        testTagId = "three_errors_btn_next",
                        modifier = Modifier.fillMaxWidth().padding(horizontal = 8.dp)
                    ) {
                        if (page < quizList.value.lastIndex) {
                            scope.launch {
                                pagerState.animateScrollToPage(
                                    page + 1,
                                    animationSpec = tween(500)
                                )
                            }
                        } else {
                            navController.popBackStack()
                        }
                    }
                }
            }
        } else {
            Column(
                modifier = Modifier
                    .fillMaxSize()
                    .align(Alignment.Center)
                    .padding(horizontal = 24.dp, vertical = 16.dp),
                horizontalAlignment = Alignment.CenterHorizontally,
                verticalArrangement = Arrangement.Center
            ) {
                Text(
                    text = stringResource(Res.string.what_happened_title),
                    style = MaterialTheme.typography.displayLarge,
                    textAlign = TextAlign.Center,
                    modifier = Modifier.fillMaxWidth().semantics { testTag = "three_errors_title" }
                )
                Text(
                    text = stringResource(Res.string.three_errors_subtitle),
                    style = MaterialTheme.typography.headlineMedium,
                    textAlign = TextAlign.Center,
                    modifier = Modifier.fillMaxWidth()
                        .semantics { testTag = "three_errors_subtitle" }
                )
                Spacer(Modifier.size(24.dp))
                AlynFeedbackBox(
                    face = Faces.Sad,
                    text = stringResource(Res.string.three_errors_feedback),
                    userData = userData
                )
            }

            Column(
                modifier = Modifier.fillMaxWidth()
                    .align(Alignment.BottomCenter)
                    .padding(horizontal = 24.dp, vertical = 16.dp),
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                GeroButton(
                    title = stringResource(Res.string.avanti_btn),
                    testTagId = "three_errors_btn_next",
                    modifier = Modifier.fillMaxWidth().height(64.dp)
                ) {
                    if (page < quizList.value.lastIndex) {
                        scope.launch {
                            pagerState.animateScrollToPage(page + 1, animationSpec = tween(500))
                        }
                    } else {
                        navController.popBackStack()
                    }
                }
            }
        }
    }
}

